import { Skeleton, Table, TableColumn } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  AssessmentLink,
  buildKnowledgeSourceFilterPath,
  ChecklistLink,
  KnowledgeNodeRelation,
  KnowledgeNodeResponse,
  KnowledgeSource,
} from '@/entities/knowledge';

type Props = {
  node?: KnowledgeNodeResponse | null;
  isLoading: boolean;
};

type RelationTableRow = {
  id: number;
  nodeId: number;
  name: string;
  type: string;
};

type SourceTableRow = {
  id: number;
  sourceId: number;
  name: string;
  type: string;
};

type ChecklistTableRow = {
  id: number;
  checklistId: number;
  name: string;
};

type AssessmentTableRow = {
  id: number;
  assessmentId: number;
  name: string;
};

export const KnowledgeRelations: FC<Props> = ({ node, isLoading }) => {
  const { t } = useTranslation('knowledge');
  const [searchParams] = useSearchParams();

  const relationRows = useMemo((): RelationTableRow[] => {
    if (!node?.relations?.length) return [];
    return node.relations.map((relation: KnowledgeNodeRelation) => ({
      id: relation.id,
      nodeId: relation.toNodeId,
      name: relation.toNodeName || t('nodeNumber', { id: relation.toNodeId }),
      type: t(`relationTypes.${relation.relationType}`, { defaultValue: relation.relationType }),
    }));
  }, [node?.relations, t]);

  const sourceRows = useMemo((): SourceTableRow[] => {
    if (!node?.sources?.length) return [];
    return node.sources.map((source: KnowledgeSource) => ({
      id: source.id,
      sourceId: source.sourceId,
      name: source.sourceName,
      type: t(`sourceTypes.${source.sourceType}`, { defaultValue: source.sourceType }),
    }));
  }, [node?.sources, t]);

  const checklistRows = useMemo((): ChecklistTableRow[] => {
    if (!node?.checklists?.length) return [];
    return node.checklists.map((checklist: ChecklistLink) => ({
      id: checklist.id,
      checklistId: checklist.checklistId,
      name: checklist.checklistName,
    }));
  }, [node?.checklists]);

  const assessmentRows = useMemo((): AssessmentTableRow[] => {
    if (!node?.assessments?.length) return [];
    return node.assessments.map((assessment: AssessmentLink) => ({
      id: assessment.id,
      assessmentId: assessment.assessmentId,
      name: assessment.assessmentName,
    }));
  }, [node?.assessments]);

  const relationColumns = useMemo(
    () =>
      [
        {
          accessorKey: 'name',
          header: () => <span>{t('linkedNode')}</span>,
          minSize: 200,
          cell: ({ row }) => (
            <NavLink to={`/knowledge/${row.original.nodeId}`} className="text-primary hover:underline">
              {row.original.name}
            </NavLink>
          ),
        },
        {
          accessorKey: 'type',
          header: () => <span>{t('relationType')}</span>,
          minSize: 150,
        },
      ] satisfies TableColumn<RelationTableRow>[],
    [t],
  );

  const sourceColumns = useMemo(
    () =>
      [
        {
          accessorKey: 'name',
          header: () => <span>{t('name')}</span>,
          minSize: 200,
          cell: ({ row }) => (
            <NavLink
              to={buildKnowledgeSourceFilterPath(row.original.sourceId, searchParams)}
              className="text-primary hover:underline"
            >
              {row.original.name}
            </NavLink>
          ),
        },
        {
          accessorKey: 'type',
          header: () => <span>{t('type')}</span>,
          minSize: 150,
        },
      ] satisfies TableColumn<SourceTableRow>[],
    [t, searchParams],
  );

  const checklistColumns = useMemo(
    () =>
      [
        {
          accessorKey: 'name',
          header: () => <span>{t('name')}</span>,
          minSize: 200,
        },
      ] satisfies TableColumn<ChecklistTableRow>[],
    [t],
  );

  const assessmentColumns = useMemo(
    () =>
      [
        {
          accessorKey: 'name',
          header: () => <span>{t('name')}</span>,
          minSize: 200,
        },
      ] satisfies TableColumn<AssessmentTableRow>[],
    [t],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const hasRelations = relationRows.length > 0;
  const hasSources = sourceRows.length > 0;
  const hasChecklists = checklistRows.length > 0;
  const hasAssessments = assessmentRows.length > 0;

  if (!hasRelations && !hasSources && !hasChecklists && !hasAssessments) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {hasRelations && (
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-semibold text-foreground">{t('relations')}</h3>
          <div className="border rounded-md overflow-hidden">
            <Table styles={{ size: 'sm' }} data={relationRows} columns={relationColumns} className="border-0" />
          </div>
        </div>
      )}

      {hasSources && (
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-semibold text-foreground">{t('sources')}</h3>
          <div className="border rounded-md overflow-hidden">
            <Table styles={{ size: 'sm' }} data={sourceRows} columns={sourceColumns} className="border-0" />
          </div>
        </div>
      )}

      {hasChecklists && (
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-semibold text-foreground">{t('checklists')}</h3>
          <div className="border rounded-md overflow-hidden">
            <Table styles={{ size: 'sm' }} data={checklistRows} columns={checklistColumns} className="border-0" />
          </div>
        </div>
      )}

      {hasAssessments && (
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-semibold text-foreground">{t('assessments')}</h3>
          <div className="border rounded-md overflow-hidden">
            <Table styles={{ size: 'sm' }} data={assessmentRows} columns={assessmentColumns} className="border-0" />
          </div>
        </div>
      )}
    </div>
  );
};
